import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowRightIcon, PlusIcon, RouteIcon, TruckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { Button } from '../../../ui/Button';
import { RegionName } from '../../../app/RegionName/RegionName';
import { usePricingSheetStore } from './store/usePricingSheetStore';
import { isFilterActive } from './types';

export interface PricingSheetEmptyProps {
  /** Optional extra spacing context (e.g. inside a tab vs a drawer). */
  className?: string;
}

/**
 * Empty-state card shown when the sheet has no rows at all. Doubles as the
 * "start here" CTA — clicking the primary button appends a new row and the
 * footer save flow takes over.
 *
 * Filter-aware behaviour: when the user has narrowed the view (origin /
 * destination / vehicles) and the resulting list is empty, we offer to add
 * **this specific lane** with the filter values pre-filled on the new row.
 * This is the only place where filters seed a new row — the footer "Add
 * row" stays a blank slate so power users can rip through entry without the
 * filter "pulling" values onto every new line.
 */
export function PricingSheetEmpty({ className }: PricingSheetEmptyProps) {
  const { t } = useTranslation(['m-pricing']);
  const filters = usePricingSheetStore(s => s.filters);
  const addRow = usePricingSheetStore(s => s.addRow);
  const clearFilters = usePricingSheetStore(s => s.clearFilters);

  const hasFilters = isFilterActive(filters);

  const handleAdd = () => addRow({ seedFromFilters: hasFilters });

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 rounded-xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center shadow-sm',
        className,
      )}>
      <div className="flex size-14 items-center justify-center rounded-full bg-primary/10 text-primary">
        <RouteIcon className="size-7" />
      </div>

      <div className="flex max-w-md flex-col gap-1.5">
        <h3 className="text-base font-semibold text-gray-900">
          {hasFilters
            ? t('sheet.empty.title_filtered', {
                defaultValue: 'No rates match this filter yet',
              })
            : t('sheet.empty.title', { defaultValue: 'No rates here yet' })}
        </h3>
        <p className="text-xs text-gray-500">
          {hasFilters
            ? t('sheet.empty.description_filtered', {
                defaultValue:
                  'We couldn\u2019t find a rate for what you searched. Add it as a new lane and we\u2019ll pre-fill the details for you.',
              })
            : t('sheet.empty.description', {
                defaultValue:
                  'Add your first lane to start building this price list. You can add as many as you like before saving.',
              })}
        </p>
      </div>

      {hasFilters && <FilterPreview filters={filters} />}

      <div className="flex flex-wrap items-center justify-center gap-2">
        <Button
          type="button"
          size="sm"
          className="h-8 gap-1.5 text-xs"
          onClick={handleAdd}>
          <PlusIcon className="size-3.5" />
          {hasFilters
            ? t('sheet.empty.cta_filtered', { defaultValue: 'Add this lane' })
            : t('sheet.empty.cta', { defaultValue: 'Add your first lane' })}
        </Button>

        {hasFilters && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 text-xs text-gray-500"
            onClick={clearFilters}>
            {t('sheet.empty.clear_filters', { defaultValue: 'Clear filters' })}
          </Button>
        )}
      </div>
    </div>
  );
}

/**
 * Chip summary of what will land on the new row when the user accepts the
 * filter-aware CTA. Keeps the action transparent so nothing is "magic".
 */
function FilterPreview({
  filters,
}: {
  filters: ReturnType<typeof usePricingSheetStore.getState>['filters'];
}) {
  const { t } = useTranslation(['m-pricing']);

  const originCode = filters.origin?.customCode;
  const destinationCode = filters.destination?.customCode;
  const originFallback = filters.origin?.displayName;
  const destinationFallback = filters.destination?.displayName;
  const vehicleCount = filters.vehicles.length;

  return (
    <div className="flex flex-wrap items-center justify-center gap-1.5 text-[11px]">
      {(originCode || destinationCode || originFallback || destinationFallback) && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 font-medium text-gray-700 ring-1 ring-gray-200">
          <span>
            {originCode ? (
              <RegionName customCode={originCode} fallback={originFallback} />
            ) : (
              originFallback ?? t('sheet.empty.any', { defaultValue: 'Any' })
            )}
          </span>
          <ArrowRightIcon className="size-3 text-gray-400 rtl:rotate-180" />
          <span>
            {destinationCode ? (
              <RegionName customCode={destinationCode} fallback={destinationFallback} />
            ) : (
              destinationFallback ?? t('sheet.empty.any', { defaultValue: 'Any' })
            )}
          </span>
        </span>
      )}
      {vehicleCount > 0 && (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-gray-50 px-2.5 py-1 font-medium text-gray-700 ring-1 ring-gray-200">
          <TruckIcon className="size-3 text-gray-400" />
          {t('sheet.empty.vehicle_count', {
            defaultValue: '{{count}} vehicle types',
            count: vehicleCount,
          })}
        </span>
      )}
    </div>
  );
}
